import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type {
  Application,
  Deployment,
  FlowMetrics,
  Incident,
  Product,
  Release,
  WorkItem,
} from './types';
import { addDays, at, clamp, pad, round, timestampBefore } from './support';

/**
 * Delivery: work items, releases, deployments, flow metrics and incidents.
 *
 * Flow metrics are derived from the generated work items and deployments rather
 * than authored beside them. It costs a little more here, but it means the
 * delivery tab's headline numbers survive someone clicking through to the
 * underlying rows — which is the first thing a sceptical engineering leader
 * does.
 */

/**
 * Sixty, not fifteen.
 *
 * Fifteen titles across 2,805 items put the same sentence on one card in every
 * seven, and a delivery board that repeats itself is one a reader stops
 * believing — along with everything else on the screen. Migration 0093 applies
 * the same list to the estate already loaded.
 */
const WORK_ITEM_TITLES = [
  'Reduce recovery option generation time',
  'Add duty-limit warning to the roster grid',
  'Harden the notification retry path',
  'Retire the legacy batch interface',
  'Improve search relevance for date-flexible trips',
  'Fix intermittent timeout on availability lookup',
  'Add audit trail to manual overrides',
  'Migrate reporting extract to the data hub',
  'Support additional currency in refunds',
  'Instrument end-to-end trace for the turn timeline',
  'Remediate high-severity dependency advisory',
  'Automate quarterly access recertification',
  'Split monolithic settlement job',
  'Add accessibility labels to the boarding view',
  'Cache fare rules at the edge',
  'Stop re-sending the disruption message on retry',
  'Carry the seat request through an involuntary reaccommodation',
  'Reconcile bag counts between the sortation feed and the manifest',
  'Expose stand closures to gate allocation within a minute',
  'Handle the leap-second rollover in duty accrual',
  'Move the nightly bid-price run ahead of the first bank',
  'Record the fare rules that applied at the moment of refund',
  'Drop the duplicate loyalty accrual on a changed booking',
  'Add a partner timeout fallback to cargo booking',
  'Return a reason code when boarding verification fails',
  'Rebuild the crew qualification forecast on establishment change',
  'Stop the catalogue serving a price the pricing team has withdrawn',
  'Backfill missing tail numbers on historic maintenance events',
  'Give the contact centre the disruption reason, not just the flag',
  'Retire the second airport code lookup',
  'Make the interline settlement adjustment reproducible',
  'Raise an alert when a stream consumer falls behind',
  'Enforce mutual exclusion between overlapping experiments',
  'Keep the previous model artefact after a deployment',
  'Apply row-level policy to the commercial domain',
  'Stop the roster publisher timing out on wide-body bases',
  'Add a manual override to de-icing sequencing',
  'Publish the schedule change before the codeshare partner is told',
  'Correct proration on interline itineraries with a surface sector',
  'Show the ancillary refund rate beside the attach rate',
  'Fail the upload when the header row is missing rather than guessing',
  'Rotate the payment provider credential without a release',
  'Age interline disputes so the value at risk is visible',
  'Emit a duty-limit breach event rather than logging it',
  'Stop double-counting a codeshare segment in route contribution',
  'Let a gate agent see why a passenger was flagged',
  'Apply retention to passenger records in the analytics store',
  'Make the fatigue score explain which duty drove it',
  'Bring the four regional check-in rule sets onto one schema',
  'Warn before a tier benefit is announced ahead of the nightly run',
  'Fix the dangerous-goods check accepting before validation returns',
  'Record the handling agent who accepted a shipment',
  'Move the weight and balance comparison into the release pipeline',
  'Stop the knowledge base serving a superseded compensation policy',
  'Make the tariff refresh reach the five largest stations on notice',
  'Give the assistance request a committed time and measure against it',
  'Take the fare quote in the assistant from the fare service',
  'Rebuild the availability cache on inventory close',
  'Log the reason a booking modification was refused',
  'Retire the third copy of the airport reference list',
];

/**
 * The day Aurora's four delivery trackers became one.
 *
 * Item records came across with their dates; two of the retired trackers used
 * issue taxonomies with no defensible mapping onto Feature, Defect, Debt and
 * Compliance, so anything opened before this date carries no flow type at all.
 * Recording nothing was the choice, because a Flow Distribution computed over
 * guessed types looks exactly like one computed over recorded types and the
 * reader cannot tell them apart.
 *
 * `deploy/migrations/0091_the_retired_trackers_left_no_flow_type.sql` applies
 * the same rule to the estate already loaded, so a regenerated dataset and a
 * migrated one agree.
 */
const TRACKER_CONSOLIDATION = '2026-01-05';

/**
 * Sixteen summaries per severity, chosen within severity and never across it.
 *
 * A severity is a promise about impact; a P1 that reads like a broken
 * documentation link is a severity nobody will trust again. One summary per
 * severity — which is what this was — put the same line on 485 of 977
 * incidents. See migration 0093.
 */
const INCIDENT_SUMMARIES: Record<string, readonly string[]> = {
  P1: [
    'Customer-impacting outage — service unavailable in one or more regions.',
    'Complete loss of service at two base airports through a departure bank.',
    'Booking unavailable on all channels; no fallback in place.',
    'Failed release rolled back after customer-visible errors on every request.',
    'Database failover did not complete; the standby came up read-only.',
    'Certificate expiry took the partner interface down without warning.',
    'Message backlog stalled the operational feed for two hours.',
    'Loss of connectivity to the mainframe estate during the morning peak.',
    'Loss of the primary payment provider; card transactions declined at checkout.',
    'Check-in unavailable at all stations for the duration of a bank.',
    'Data centre power event took the primary region offline.',
    'Corrupted reference load pushed bad airport data to every consumer.',
    'Credential rotation locked the platform out of its own database.',
    'Rate limiting at the upstream provider refused every request for an hour.',
    'Crew rostering unavailable during a disruption; recovery run by hand.',
    'Baggage sortation halted at a base airport through the morning wave.',
  ],
  P2: [
    'Degraded service — elevated error rate and latency affecting a subset of users.',
    'Response times above the agreed threshold for a third of requests.',
    'Intermittent failures on one region only; retries mostly succeeding.',
    'Batch job overran and finished after the window it feeds.',
    'Partner interface returning errors on a minority of requests.',
    'Cache invalidation missed, so a stale price was served for several hours.',
    'Queue depth grew until consumers fell behind their retention window.',
    'One node removed from service after memory pressure; capacity reduced.',
    'Search results degraded to a cached set for part of the day.',
    'Notification delivery delayed by up to forty minutes on one channel.',
    'Reporting extract incomplete; two source feeds arrived after the cut-off.',
    'A deployment reduced capacity below the safe level for twenty minutes.',
    'Session expiry set too short after a configuration change.',
    'Duplicate messages emitted after a consumer restart.',
    'Third-party lookup timed out intermittently through the evening peak.',
    'Background reconciliation fell a day behind and caught up overnight.',
  ],
  P3: [
    'Minor defect — workaround available, no customer impact recorded.',
    'Display error on an internal screen; the underlying figures were correct.',
    'Scheduled job failed once and succeeded on the automatic retry.',
    'Alert fired on a threshold that was set too low; no service impact.',
    'A single record failed to synchronise and was corrected by hand.',
    'Report ran late; the data behind it was complete and unaffected.',
    'Log noise from a deprecated interface with no consumer left.',
    'Documentation link broken after a content migration.',
    'A monitoring probe failed and recovered before anybody was paged.',
    'Timezone shown incorrectly on one internal report.',
    'A stale feature flag left an unused code path enabled.',
    'Disk usage crossed a warning threshold and was cleared automatically.',
    'A pre-production environment was unavailable; no customer impact.',
    'A retired endpoint was still being called by an internal script.',
    'Sort order on a list screen did not match the column header.',
    'An email template rendered an unresolved token that no recipient could see.',
  ],
};

export interface DeliveryData {
  workItems: WorkItem[];
  releases: Release[];
  deployments: Deployment[];
  flowMetrics: FlowMetrics[];
  incidents: Incident[];
}

/**
 * What a release was for, twenty-four ways.
 *
 * Four of these were the whole list until migration 0097, which is why a
 * product with ten releases had two or three pairs of identically named rows
 * sitting a fortnight apart on the calendar. Ordered so the first four are the
 * ones the estate already used; the assignment below walks the list by index
 * rather than hashing into it, so no product ever draws the same theme twice.
 */
const RELEASE_THEMES = [
  'enhancements',
  'compliance update',
  'platform uplift',
  'performance work',
  'resilience hardening',
  'data quality corrections',
  'accessibility remediation',
  'integration refresh',
  'observability rollout',
  'security patching',
  'defect burndown',
  'schema migration',
  'mobile parity',
  'reporting refresh',
  'authentication cutover',
  'capacity increase',
  'interface versioning',
  'audit trail extension',
  'localisation rollout',
  'recovery rehearsal',
  'hosting cost reduction',
  'screen modernisation',
  'batch window reduction',
  'retry and backoff rework',
];

export function buildDelivery(
  asOf: string,
  rng: Rng,
  products: readonly Product[],
  applications: readonly Application[],
): DeliveryData {
  const deliveryRng = rng.fork('delivery');
  const stamp = provenanceFactory(asOf, rng.fork('delivery-provenance'));

  const workItems: WorkItem[] = [];
  const releases: Release[] = [];
  const deployments: Deployment[] = [];
  const flowMetrics: FlowMetrics[] = [];
  const incidents: Incident[] = [];

  const active = products.filter((p) => p.lifecycle === 'Active' || p.lifecycle === 'Sunset');
  const appsByCode = new Map(applications.map((a) => [a.businessCode, a]));

  let workSeq = 0;
  let releaseSeq = 0;
  let deploySeq = 0;
  let incidentSeq = 0;

  for (const product of active) {
    const isCurated = product.isCurated;
    const healthFactor = clamp(product.healthScore / 80, 0.35, 1.25);

    // ── Work items ──────────────────────────────────────────────────────────
    const openCount = isCurated ? deliveryRng.int(18, 52) : deliveryRng.int(4, 22);
    for (let i = 0; i < openCount; i += 1) {
      workSeq += 1;
      const stage = deliveryRng.weighted([
        ['Plan', 18],
        ['Build', 26],
        ['Test', 16],
        ['Deploy', 10],
        ['Done', 30],
      ] as const);
      const openedDaysAgo = deliveryRng.int(3, 180);
      const isClosed = stage === 'Done';
      const cycleDays = Math.round(deliveryRng.around(18 / healthFactor, 12, 2, 130));
      const code = `WI-${pad(workSeq, 6)}`;
      const openedOn = addDays(asOf, -openedDaysAgo);
      const flowType = deliveryRng.weighted([
        ['Feature', 52],
        ['Defect', 26],
        ['Debt', 15],
        ['Compliance', 7],
      ] as const);
      workItems.push({
        id: idFor('WorkItem', code),
        businessCode: code,
        productCode: product.businessCode,
        title: at(WORK_ITEM_TITLES, workSeq % WORK_ITEM_TITLES.length),
        // Null, not a default. See TRACKER_CONSOLIDATION above.
        type: openedOn < TRACKER_CONSOLIDATION ? null : flowType,
        stage,
        points: deliveryRng.weighted([
          [1, 14],
          [2, 24],
          [3, 26],
          [5, 22],
          [8, 11],
          [13, 3],
        ] as const),
        openedOn,
        closedOn: isClosed ? addDays(asOf, -Math.max(0, openedDaysAgo - cycleDays)) : null,
        provenance: stamp(SOURCE_SYSTEMS.workTracker, `WT-${pad(workSeq, 7)}`, {
          meanAgeDays: 2,
          meanConfidence: 0.91,
        }),
      });
    }

    // ── Releases ────────────────────────────────────────────────────────────
    const releaseCount = isCurated ? deliveryRng.int(5, 9) : deliveryRng.int(1, 4);
    for (let i = 0; i < releaseCount; i += 1) {
      releaseSeq += 1;
      const offset = deliveryRng.int(-120, 110);
      const code =
        product.businessCode === 'PRD-CDR'
          ? `R26.${pad(6 + i, 2)}`
          : `R26.${pad(deliveryRng.int(1, 12), 2)}-${pad(releaseSeq % 97, 2)}`;
      const status: Release['status'] =
        offset < -14
          ? 'Released'
          : offset < 0
            ? 'InFlight'
            : deliveryRng.chance(0.06)
              ? 'Cancelled'
              : 'Planned';
      releases.push({
        id: idFor('Release', `${product.businessCode}-${code}-${releaseSeq}`),
        businessCode: code,
        productCode: product.businessCode,
        name:
          product.businessCode === 'PRD-CDR'
            ? at(
                [
                  'Crew Swap Automation',
                  'Reserve Optimization',
                  'Fatigue Rule Engine Enhancements',
                  'Recovery Option Explainability',
                  'Crew Travel Rebooking',
                ],
                i % 5,
              )
            : `${product.name} ${RELEASE_THEMES[i % RELEASE_THEMES.length]}`,
        plannedDate: addDays(asOf, offset),
        actualDate: status === 'Released' ? addDays(asOf, offset + deliveryRng.int(0, 9)) : null,
        status,
        provenance: stamp(SOURCE_SYSTEMS.workTracker, `WT-REL-${pad(releaseSeq, 6)}`, {
          meanAgeDays: 3,
          meanConfidence: 0.89,
        }),
      });
    }

    // ── Deployments ─────────────────────────────────────────────────────────
    const deployCount = Math.max(
      3,
      Math.round(deliveryRng.around(isCurated ? 46 : 12, isCurated ? 22 : 8, 3, 120)),
    );
    let failures = 0;
    let leadTimeTotal = 0;
    for (let i = 0; i < deployCount; i += 1) {
      deploySeq += 1;
      const succeeded = !deliveryRng.chance(clamp(0.16 / healthFactor, 0.01, 0.34));
      if (!succeeded) failures += 1;
      const leadTimeHours = round(deliveryRng.around(72 / healthFactor, 60, 2, 900), 1);
      leadTimeTotal += leadTimeHours;
      const code = `DEP-${pad(deploySeq, 6)}`;
      deployments.push({
        id: idFor('Deployment', code),
        businessCode: code,
        productCode: product.businessCode,
        occurredAt: timestampBefore(asOf, deliveryRng.int(0, 180), deliveryRng.int(0, 1400)),
        succeeded,
        leadTimeHours,
        provenance: stamp(SOURCE_SYSTEMS.workTracker, `WT-DEP-${pad(deploySeq, 7)}`, {
          meanAgeDays: 1,
          meanConfidence: 0.95,
        }),
      });
    }

    // ── Incidents ───────────────────────────────────────────────────────────
    const incidentCount = Math.max(
      0,
      Math.round(deliveryRng.around(isCurated ? 9 : 3, 5, 0, 26) / healthFactor),
    );
    let restoreTotal = 0;
    let restoreCount = 0;
    for (let i = 0; i < incidentCount; i += 1) {
      incidentSeq += 1;
      const severity = deliveryRng.weighted([
        ['P1', product.health === 'Healthy' ? 6 : 18],
        ['P2', 34],
        ['P3', 48],
      ] as const);
      const openedDaysAgo = deliveryRng.int(0, 120);
      const resolved = openedDaysAgo > 1 || deliveryRng.chance(0.7);
      const minutes = Math.round(
        deliveryRng.around(severity === 'P1' ? 148 : severity === 'P2' ? 96 : 320, 90, 12, 2600),
      );
      if (resolved) {
        restoreTotal += minutes;
        restoreCount += 1;
      }
      const appCode =
        deliveryRng.chance(0.75) && applications.length > 0
          ? deliveryRng.pick(applications).businessCode
          : null;
      const code = `INC-${pad(incidentSeq, 6)}`;
      incidents.push({
        id: idFor('Incident', code),
        businessCode: code,
        productCode: product.businessCode,
        applicationCode: appCode && appsByCode.has(appCode) ? appCode : null,
        severity,
        openedAt: timestampBefore(asOf, openedDaysAgo, deliveryRng.int(0, 1400)),
        resolvedAt: resolved
          ? timestampBefore(asOf, openedDaysAgo, deliveryRng.int(0, 1400) - minutes)
          : null,
        minutesToRestore: resolved ? minutes : null,
        summary: ((list) => at(list, incidentSeq % list.length))(
          INCIDENT_SUMMARIES[severity] ?? ['Service event recorded.'],
        ),
        provenance: stamp(SOURCE_SYSTEMS.serviceDesk, `SD-${pad(incidentSeq, 7)}`, {
          meanAgeDays: 1,
          meanConfidence: 0.92,
        }),
      });
    }

    // ── Flow metrics, computed from the rows above ──────────────────────────
    const closed = workItems.filter(
      (w) => w.productCode === product.businessCode && w.closedOn !== null,
    );
    const productItems = workItems.filter((w) => w.productCode === product.businessCode);
    const observationMonths = 6;
    flowMetrics.push({
      productCode: product.businessCode,
      deploymentFrequencyPerMonth: round(deployCount / observationMonths, 2),
      leadTimeDays: round(leadTimeTotal / deployCount / 24, 2),
      changeFailureRate: round(failures / deployCount, 4),
      timeToRestoreHours: restoreCount === 0 ? 0 : round(restoreTotal / restoreCount / 60, 2),
      throughputItemsPerMonth: round(closed.length / observationMonths, 2),
      flowEfficiency: round(
        clamp(productItems.length === 0 ? 0 : closed.length / productItems.length, 0, 1),
        4,
      ),
      provenance: stamp(SOURCE_SYSTEMS.workTracker, `WT-FLOW-${product.businessCode}`, {
        meanAgeDays: 1,
        meanConfidence: 0.86,
      }),
    });
  }

  return { workItems, releases, deployments, flowMetrics, incidents };
}
